import {
    OrganizationCardState,
    type PaymentInstrumentFragmentFragment,
    type PreviewCreditFragmentFragment,
    type SavedPaymentMethodFragmentFragment,
} from 'data/__generated__';

import { DetailLink, DetailValue } from 'components/DetailsView';
import { getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { getPaymentMethodLabel } from 'helpers/paymentMethods';
import { formatPrice } from 'utils/price';

import { CREDIT_SOURCE_LABELS, CREDIT_TYPE_LABELS } from 'pages/Credits/constants';

export const SavedPaymentMethodDetails = ({
    savedPaymentMethod,
}: {
    savedPaymentMethod: SavedPaymentMethodFragmentFragment;
}) => (
    <>
        <DetailValue label="Libellé" value={savedPaymentMethod.label || '-'} />
        <DetailValue label="Fournisseur" value={`${savedPaymentMethod.provider} (${savedPaymentMethod.paymentType})`} />
        {savedPaymentMethod.brand ? <DetailValue label="Marque" value={savedPaymentMethod.brand} /> : null}
        <DetailValue label="Expiration" value={savedPaymentMethod.expiration ?? '-'} />
        <DetailValue label="Par défaut" value={savedPaymentMethod.isDefault ? 'Oui' : 'Non'} />
        <DetailValue label="Refus consécutifs" value={savedPaymentMethod.retries} />
        <DetailValue
            label="État"
            value={
                savedPaymentMethod.archivedAt
                    ? `Archivé le ${getExtendedFormattedDatetime(savedPaymentMethod.archivedAt)}`
                    : 'Actif'
            }
        />
        <DetailValue label="Enregistré le" value={getExtendedFormattedDatetime(savedPaymentMethod.createdAt)} />
        {savedPaymentMethod.user ? (
            <DetailLink
                label="Utilisateur"
                name={savedPaymentMethod.user.profile.fullname ?? undefined}
                value={savedPaymentMethod.user._id}
                path="user"
            />
        ) : null}
    </>
);

export const CreditDetails = ({ credit }: { credit: PreviewCreditFragmentFragment }) => (
    <>
        <DetailValue label="Nom" value={credit.name} />
        <DetailValue label="Type" value={CREDIT_TYPE_LABELS[credit.type]} />
        {credit.source ? <DetailValue label="Source" value={CREDIT_SOURCE_LABELS[credit.source]} /> : null}
        <DetailValue label="Montant initial" value={formatPrice(credit.initialAmount)} />
        <DetailValue label="Montant restant" value={formatPrice(credit.currentAmount)} />
        <DetailValue label="Expiration" value={getExtendedFormattedDatetime(credit.expirationDate)} />
        <DetailValue label="Créé le" value={getExtendedFormattedDatetime(credit.createdAt)} />
        {credit.refundedAt ? (
            <DetailValue label="Remboursé le" value={getExtendedFormattedDatetime(credit.refundedAt)} />
        ) : null}
        {credit.owner ? (
            <DetailLink
                label="Propriétaire"
                name={credit.owner.profile.fullname ?? undefined}
                value={credit.owner._id}
                path="user"
            />
        ) : null}
        {credit.createdBy ? (
            <DetailLink
                label="Créé par"
                name={credit.createdBy.profile.fullname ?? undefined}
                value={credit.createdBy._id}
                path="user"
            />
        ) : null}
        {credit.organizationBatched ? (
            <DetailValue label="Distribué par l'organisation" value={credit.organizationBatched.name} />
        ) : null}
    </>
);

// Un seul des documents est renseigné, celui que désigne `kind` : chaque moyen de paiement vit dans sa collection.
// Subventions, rituels et formules ont déjà leur fiche : on y renvoie plutôt que d'en recopier le détail.
export const PaymentInstrumentDetails = ({ instrument }: { instrument: PaymentInstrumentFragmentFragment }) => {
    const { kind, savedPaymentMethod, credit, organizationCard, subvention, ritual, mealPlanDefinition } = instrument;

    return (
        <>
            <DetailValue label="Moyen de paiement" value={getPaymentMethodLabel(kind)} />
            {savedPaymentMethod ? <SavedPaymentMethodDetails savedPaymentMethod={savedPaymentMethod} /> : null}
            {credit ? <CreditDetails credit={credit} /> : null}
            {organizationCard ? (
                <>
                    <DetailValue
                        label="État"
                        value={organizationCard.state === OrganizationCardState.Active ? 'Active' : 'Archivée'}
                    />
                    {organizationCard.organization ? (
                        <DetailLink
                            label="Organisation"
                            name={organizationCard.organization.name}
                            value={organizationCard.organization._id}
                            path="organization"
                        />
                    ) : null}
                </>
            ) : null}
            {subvention ? (
                <DetailLink label="Subvention" name={subvention.name} value={subvention._id} path="subvention" />
            ) : null}
            {ritual ? <DetailLink label="Rituel" name={ritual.name} value={ritual._id} path="ritual" /> : null}
            {mealPlanDefinition ? (
                <DetailLink
                    label="Formule"
                    name={mealPlanDefinition.name}
                    value={mealPlanDefinition._id}
                    path="meal-plan-definition"
                />
            ) : null}
        </>
    );
};
